import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  CreditCard,
  Calendar,
  X,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Loader2
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Search, Filter } from "lucide-react";

interface Payment {
  id: string;
  mollie_payment_id: string;
  amount: number;
  currency: string;
  status: string;
  payment_type: string;
  metadata: any;
  created_at: string;
  updated_at: string;
  paid_at: string | null;
  end_date?: string | null; // For premium subscriptions and level boosts
}

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  premium_monthly: "Premium Subscription (Monthly)",
  premium_yearly: "Premium Subscription (Yearly)",
  level2_boost: "Level 2 Boost",
  level3_boost: "Level 3 Boost",
  level4_boost: "Level 4 Boost",
  twitter_post: "Twitter Post Promotion",
  newsletter_basic: "Newsletter Basic Promotion",
  press_kit: "Press Kit Auto-Builder",
  creator_push: "Creator Push Promotion",
};

export default function MySubscriptions() {
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [cancellingPaymentId, setCancellingPaymentId] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [paymentToCancel, setPaymentToCancel] = useState<Payment | null>(null);
  const [cancelConfirmation, setCancelConfirmation] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    if (user?.id) {
      fetchPayments();
      checkAdminStatus();
    }
  }, [user?.id]);

  const checkAdminStatus = async () => {
    if (!user?.id) return;
    try {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin, display_name")
        .eq("id", user.id)
        .single();

      setIsAdmin(data?.is_admin === true || data?.display_name === 'miikeu');
    } catch (error) {
      console.error("Error checking admin status:", error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchPayments();
    }
  }, [user?.id]);

  const fetchPayments = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const { data: paymentsData, error } = await supabase
        .from("payments")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching payments:", error);
        toast.error("Failed to load payments");
        setPayments([]);
        return;
      }

      // Fetch additional data for end dates
      const paymentsWithEndDates = await Promise.all(
        (paymentsData || []).map(async (payment) => {
          let endDate: string | null = null;

          // For premium subscriptions, get premium_end_date from profile
          if (payment.payment_type.startsWith("premium_") && payment.status === "paid") {
            const { data: profile } = await supabase
              .from("profiles")
              .select("premium_end_date")
              .eq("id", user.id)
              .single();

            if (profile?.premium_end_date) {
              endDate = profile.premium_end_date;
            }
          }
          // For level boosts, get highlight_end_date from game
          else if (
            payment.payment_type.startsWith("level") &&
            payment.payment_type.endsWith("_boost") &&
            payment.status === "paid"
          ) {
            let metadata = payment.metadata;
            if (typeof metadata === 'string') {
              try {
                metadata = JSON.parse(metadata);
              } catch (e) {
                metadata = {};
              }
            }

            const gameId = metadata?.game_id;
            if (gameId) {
              const { data: game } = await supabase
                .from("games")
                .select("highlight_end_date")
                .eq("id", gameId)
                .eq("developer_id", user.id)
                .single();

              if (game?.highlight_end_date) {
                endDate = game.highlight_end_date;
              }
            }
          }

          return {
            ...payment,
            end_date: endDate,
          };
        })
      );

      // Filter out pending and open payments
      const filteredPayments = paymentsWithEndDates.filter(
        payment => payment.status !== 'pending' && payment.status !== 'open'
      );
      setPayments(filteredPayments);
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast.error("Failed to load payments");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const canCancelPayment = (payment: Payment): boolean => {
    // Only pending or open payments can be cancelled by anyone
    if (payment.status === "pending" || payment.status === "open") {
      return true;
    }

    // For paid premium subscriptions, everyone can cancel (but only admins can revoke instantly)
    if (payment.status === "paid" && payment.payment_type.startsWith("premium_")) {
      return true;
    }

    return false;
  };

  const handleCancelClick = (payment: Payment) => {
    setPaymentToCancel(payment);
    setShowCancelDialog(true);
    setCancelConfirmation("");
  };

  const handleCancelPayment = async () => {
    if (!paymentToCancel || !user?.id) return;

    // Validate confirmation text
    if (cancelConfirmation.toLowerCase() !== "cancel") {
      toast.error("Please type 'CANCEL' to confirm cancellation");
      return;
    }

    setCancellingPaymentId(paymentToCancel.id);

    try {
      // For pending/open payments, mark as cancelled
      if (paymentToCancel.status === "pending" || paymentToCancel.status === "open") {
        const { error } = await supabase
          .from("payments")
          .update({ status: "canceled" })
          .eq("id", paymentToCancel.id);

        if (error) {
          throw error;
        }

        toast.success("Payment cancelled successfully");
      }
      // For paid premium subscriptions
      else if (paymentToCancel.status === "paid" && paymentToCancel.payment_type.startsWith("premium_")) {
        if (isAdmin) {
          // Admin can cancel premium subscription instantly
          const { error: profileError } = await supabase
            .from("profiles")
            .update({
              is_premium: false,
              premium_end_date: new Date().toISOString(),
            })
            .eq("id", user.id);

          if (profileError) {
            throw profileError;
          }

          const { error: paymentError } = await supabase
            .from("payments")
            .update({ status: "canceled" })
            .eq("id", paymentToCancel.id);

          if (paymentError) {
            throw paymentError;
          }

          toast.success("Premium subscription cancelled successfully");
        } else {
          // Non-admin: mark subscription for non-renewal (don't revoke immediately)
          let metadata = paymentToCancel.metadata;
          if (typeof metadata === 'string') {
            try {
              metadata = JSON.parse(metadata);
            } catch (e) {
              metadata = {};
            }
          }

          const updatedMetadata = {
            ...metadata,
            cancel_renewal: true,
            cancel_renewal_at: new Date().toISOString()
          };

          const { error: paymentError } = await supabase
            .from("payments")
            .update({
              metadata: updatedMetadata
            })
            .eq("id", paymentToCancel.id);

          if (paymentError) {
            throw paymentError;
          }

          toast.success("Subscription renewal cancelled. Your premium access will continue until the end of your current billing period.");
        }
      }

      setShowCancelDialog(false);
      setPaymentToCancel(null);
      setCancelConfirmation("");
      await fetchPayments();
    } catch (error: any) {
      console.error("Error cancelling payment:", error);
      toast.error(error.message || "Failed to cancel payment");
    } finally {
      setCancellingPaymentId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
      case "pending":
      case "open":
        return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case "failed":
      case "expired":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      case "canceled":
        return <Badge variant="secondary"><X className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Filter payments
  const filteredPayments = payments.filter((payment) => {
    // Status filter
    if (statusFilter !== "all" && payment.status !== statusFilter) {
      return false;
    }

    // Type filter
    if (typeFilter !== "all" && payment.payment_type !== typeFilter) {
      return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const typeLabel = PAYMENT_TYPE_LABELS[payment.payment_type] || payment.payment_type;
      const amount = formatAmount(payment.amount, payment.currency);
      if (
        !typeLabel.toLowerCase().includes(query) &&
        !amount.toLowerCase().includes(query) &&
        !payment.mollie_payment_id.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    return true;
  });

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "EUR",
    }).format(amount);
  };

  const getUniquePaymentTypes = () => {
    const types = new Set(payments.map(p => p.payment_type));
    return Array.from(types);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-1">My Subscriptions & Purchases</h1>
          <p className="text-sm text-muted-foreground">
            Manage your active subscriptions and view your purchase history
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="search" className="text-xs">Search</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    id="search"
                    placeholder="Search payments..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="status" className="text-xs">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger id="status" className="h-9 text-sm mt-1">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    {/* <SelectItem value="canceled">Canceled</SelectItem> */}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="type" className="text-xs">Type</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger id="type" className="h-9 text-sm mt-1">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {getUniquePaymentTypes().map((type) => (
                      <SelectItem key={type} value={type}>
                        {PAYMENT_TYPE_LABELS[type] || type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setStatusFilter("all");
                    setTypeFilter("all");
                    setSearchQuery("");
                  }}
                  className="h-9 w-full"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payments Table - Desktop */}
        {filteredPayments.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <Card className="hidden md:block">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Type</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Amount</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Created</th>
                        <th className="text-left p-3 text-xs font-medium text-muted-foreground">Paid</th>
                        {filteredPayments.some(p =>
                          (p.payment_type.startsWith("premium_") ||
                            (p.payment_type.startsWith("level") && p.payment_type.endsWith("_boost"))) &&
                          p.status === "paid" && p.end_date
                        ) && (
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground">End Date</th>
                          )}
                        {/* <th className="text-right p-3 text-xs font-medium text-muted-foreground">Actions</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((payment) => (
                        <tr key={payment.id} className="border-b hover:bg-muted/30 transition-colors">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium">
                                {PAYMENT_TYPE_LABELS[payment.payment_type] || payment.payment_type}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className="text-sm font-semibold">
                              {formatAmount(payment.amount, payment.currency)}
                            </span>
                          </td>
                          <td className="p-3">
                            {getStatusBadge(payment.status)}
                          </td>
                          <td className="p-3">
                            <span className="text-xs text-muted-foreground">
                              {formatDate(payment.created_at)}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-xs text-muted-foreground">
                              {payment.paid_at ? formatDate(payment.paid_at) : "-"}
                            </span>
                          </td>
                          {filteredPayments.some(p =>
                            (p.payment_type.startsWith("premium_") ||
                              (p.payment_type.startsWith("level") && p.payment_type.endsWith("_boost"))) &&
                            p.status === "paid" && p.end_date
                          ) && (
                              <td className="p-3">
                                {(payment.payment_type.startsWith("premium_") ||
                                  (payment.payment_type.startsWith("level") && payment.payment_type.endsWith("_boost"))) &&
                                  payment.status === "paid" && payment.end_date ? (
                                  <span className="text-xs text-muted-foreground">
                                    {formatDate(payment.end_date)}
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </td>
                            )}
                          {/* <td className="p-3">
                            <div className="flex justify-end">
                              {canCancelPayment(payment) && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleCancelClick(payment)}
                                  disabled={cancellingPaymentId === payment.id}
                                  className="h-7 text-xs"
                                >
                                  {cancellingPaymentId === payment.id ? (
                                    <>
                                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                      Cancelling...
                                    </>
                                  ) : (
                                    <>
                                      <X className="w-3 h-3 mr-1" />
                                      Cancel
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {filteredPayments.map((payment) => (
                <Card key={payment.id}>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <CreditCard className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm font-medium truncate">
                            {PAYMENT_TYPE_LABELS[payment.payment_type] || payment.payment_type}
                          </span>
                        </div>
                        {getStatusBadge(payment.status)}
                      </div>

                      {/* Amount */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Amount</span>
                        <span className="text-base font-semibold">
                          {formatAmount(payment.amount, payment.currency)}
                        </span>
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-muted-foreground block mb-1">Created</span>
                          <span>{formatDate(payment.created_at)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block mb-1">Paid</span>
                          <span>{payment.paid_at ? formatDate(payment.paid_at) : "-"}</span>
                        </div>
                      </div>

                      {/* End Date */}
                      {(payment.payment_type.startsWith("premium_") ||
                        (payment.payment_type.startsWith("level") && payment.payment_type.endsWith("_boost"))) &&
                        payment.status === "paid" && payment.end_date && (
                          <div className="pt-2 border-t">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                End Date
                              </span>
                              <span className="text-xs font-medium">
                                {formatDate(payment.end_date)}
                              </span>
                            </div>
                          </div>
                        )}

                      {/* Actions */}
                      {canCancelPayment(payment) && (
                        <div className="pt-2 border-t">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleCancelClick(payment)}
                            disabled={cancellingPaymentId === payment.id}
                            className="w-full h-8 text-xs"
                          >
                            {cancellingPaymentId === payment.id ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                Cancelling...
                              </>
                            ) : (
                              <>
                                <X className="w-3 h-3 mr-1" />
                                Cancel
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <Card>
            <CardContent className="py-8 text-center">
              <CreditCard className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {payments.length === 0 ? "No payments found" : "No payments match your filters"}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Cancel Confirmation Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Cancel Subscription/Payment
              </DialogTitle>
              <DialogDescription>
                Please read the following information carefully before proceeding.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Important Information</AlertTitle>
                <AlertDescription>
                  {paymentToCancel?.payment_type.startsWith("premium_") ? (
                    <>
                      <p className="mb-2">
                        By cancelling your premium subscription renewal, you acknowledge that:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Your premium access will remain active until the end of your current billing period ({paymentToCancel.end_date ? formatDate(paymentToCancel.end_date) : 'end date'})</li>
                        <li>You will not be charged for future billing cycles</li>
                        <li>All premium features will be disabled after the current period ends</li>
                        <li>This action prevents automatic renewal but does not revoke your current access</li>
                        <li>No refunds will be issued for the current billing period</li>
                        {isAdmin && (
                          <li className="text-amber-500 font-medium">⚠️ As an admin, this will immediately revoke your premium access</li>
                        )}
                      </ul>
                    </>
                  ) : (
                    <>
                      <p className="mb-2">
                        By cancelling this payment, you acknowledge that:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>This payment will be cancelled and will not be processed</li>
                        <li>Any associated benefits or services will not be activated</li>
                        <li>This action cannot be undone</li>
                        <li>If the payment was already processed, cancellation may not be possible</li>
                      </ul>
                    </>
                  )}
                </AlertDescription>
              </Alert>

              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">Legal Terms:</p>
                <p className="text-xs text-muted-foreground">
                  By proceeding with the cancellation, you agree to the terms and conditions
                  of our service. Cancellation requests are processed in accordance with our
                  refund policy and applicable consumer protection laws. For subscription services,
                  cancellation takes effect at the end of the current billing period.
                  No partial refunds will be issued for the current period.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Type <strong>CANCEL</strong> to confirm:
                </label>
                <Input
                  type="text"
                  value={cancelConfirmation}
                  onChange={(e) => setCancelConfirmation(e.target.value)}
                  placeholder="Type CANCEL here"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelDialog(false);
                  setPaymentToCancel(null);
                  setCancelConfirmation("");
                }}
              >
                Keep Subscription
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelPayment}
                disabled={
                  cancelConfirmation.toLowerCase() !== "cancel" ||
                  cancellingPaymentId !== null
                }
              >
                {cancellingPaymentId ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Confirm Cancellation"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

